import type { SelectorMode } from './types';

export interface SelectorCategory {
	key: string;
	name: string;
	color: string;
	target: boolean;
}

export interface WheelSlice extends SelectorCategory {
	path: string;
	labelX: number;
	labelY: number;
	labelRotation: number;
}

export const selectorDurations = (reducedMotion: boolean) => ({
	cardFlip: reducedMotion ? 10 : 170,
	cardReady: reducedMotion ? 30 : 1120,
	wheel: reducedMotion ? 900 : 6000,
	slot: reducedMotion ? 900 : 3800,
	slotSettle: reducedMotion ? 30 : 560,
});

const shuffle = <T>(items: T[], random: () => number): T[] => {
	const result = [...items];
	for( let i = result.length - 1; i > 0; i-- ){
		const j = Math.floor( random() * ( i + 1 ) );
		[result[i], result[j]] = [result[j], result[i]];
	}
	return result;
};

export const buildVisibleCategories = (
	target: Omit<SelectorCategory, 'key' | 'target'>,
	candidates: Array<Omit<SelectorCategory, 'key' | 'target'>>,
	mode: SelectorMode,
	random: () => number = Math.random,
): SelectorCategory[] => {
	const targetCategory: SelectorCategory = { ...target, key: 'target', target: true };
	if( mode === 'cards' ){
		return [0, 1, 2].map((index) => ({ ...targetCategory, key: `card-${index}` }));
	}

	const limit = mode === 'wheel' ? 6 : 10;
	const names = new Set([target.name]);
	const decoys = candidates.filter((category) => {
		if( names.has(category.name) ) return false;
		names.add(category.name);
		return true;
	});
	const base = shuffle([
		targetCategory,
		...shuffle(decoys, random).slice( 0, Math.max( 0, limit - 1 ) ).map((category, index) => ({
			...category,
			key: `decoy-${index}`,
			target: false,
		})),
	], random);
	if( mode === 'slot' ) return base;

	const wheel = [...base];
	while( wheel.length < 6 ){
		const source = base[wheel.length % base.length] || targetCategory;
		wheel.push({ ...source, key: `${source.key}-repeat-${wheel.length}` });
	}
	return wheel.slice( 0, 6 );
};

export const polarToCartesian = (radius: number, angleDegrees: number) => {
	const radians = ( angleDegrees - 90 ) * Math.PI / 180;
	return { x: radius * Math.cos(radians), y: radius * Math.sin(radians) };
};

export const createSlicePath = (startAngle: number, endAngle: number, radius = 104): string => {
	const start = polarToCartesian(radius, startAngle);
	const end = polarToCartesian(radius, endAngle);
	return [
		'M 0 0',
		`L ${start.x.toFixed(3)} ${start.y.toFixed(3)}`,
		`A ${radius} ${radius} 0 ${endAngle - startAngle > 180 ? 1 : 0} 1 ${end.x.toFixed(3)} ${end.y.toFixed(3)}`,
		'Z',
	].join(' ');
};

export const buildWheelSlices = (categories: SelectorCategory[]): WheelSlice[] => {
	const sliceAngle = 360 / categories.length;
	return categories.map((category, index) => {
		const startAngle = index * sliceAngle;
		const labelAngle = startAngle + sliceAngle / 2;
		const label = polarToCartesian(58, labelAngle);
		return {
			...category,
			path: createSlicePath(startAngle, startAngle + sliceAngle),
			labelX: label.x,
			labelY: label.y,
			labelRotation: labelAngle + 90,
		};
	});
};

export const wheelTargetRotation = (
	selectedIndex: number,
	sliceCount: number,
	currentRotation = 0,
	reducedMotion = false,
): number => {
	const sliceAngle = 360 / sliceCount;
	const selectedCenter = selectedIndex * sliceAngle + sliceAngle / 2;
	const normalizedTarget = ( ( -selectedCenter % 360 ) + 360 ) % 360;
	const normalizedCurrent = ( ( currentRotation % 360 ) + 360 ) % 360;
	const delta = ( normalizedTarget - normalizedCurrent + 360 ) % 360;
	return currentRotation + ( reducedMotion ? 1 : 7 ) * 360 + delta;
};

export const repeatSlotCategories = (categories: SelectorCategory[], repeats = 8): SelectorCategory[] =>
	Array.from({ length: repeats }, (_, repeatIndex) =>
		categories.map((category, categoryIndex) => ({
			...category,
			key: `${category.key}-slot-${repeatIndex}-${categoryIndex}`,
		})),
	).flat();

export const slotTargetIndex = (
	categories: SelectorCategory[],
	currentIndex: number,
	reducedMotion = false,
): number => {
	const currentCategoryIndex = currentIndex % categories.length;
	const targetIndex = Math.max( 0, categories.findIndex((category) => category.target) );
	const steps = ( targetIndex - currentCategoryIndex + categories.length ) % categories.length;
	return currentIndex + ( reducedMotion ? 1 : 4 ) * categories.length + steps;
};
